import { useMemo } from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, ChevronDown, ExternalLink } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { container, gutters } from "@/components/ds";
import { cn } from "@/lib/utils";
import { MORE_GOALS, useHomeCompounds } from "@/components/home/homeData";
import { DotSep, HomeHeading, homeSectionY, Skeleton, SubHeading } from "@/components/home/parts";

/**
 * Homepage latest, the page's one ending: three blog posts, news when there is any recent, and a closed
 * "Browse more" list. No signup here: the cheat sheet form in the hero is the page's signup.
 *
 * - Posts and news are plain text cards and rows: no cover images to wait for. The cards keep their height while
 *   the posts load. Since direction B (2026-10-24) each post is a white card with a soft shadow, and the news rows sit
 *   on one card.
 * - The old "Talk to other researchers" block is gone as a block: its three links (two subreddits and the Discord)
 *   are in the navbar's community group on every page, r/BodyHackGuide is in the announcement strip and the footer
 *   carries the Discord and r/BodyHackGuide. They are also at the end of "Browse more", because the navbar renders
 *   its group only while its menu is open.
 * - Browse more holds the links the desktop-only "Pick your research path" table carried (the buying guide, coupon,
 *   protocol and safety page of each featured compound, and the three hub links), now on phones too, and on a phone
 *   the five goals that show as chips under the goal tiles from 640px. It is a <details> element, so the links are in
 *   the page for crawlers while it is closed.
 */
type Post = { id: string; title: string; slug: string; summary: string | null; category: string | null; created_at: string };
type News = { id: string; title: string; source_name: string | null; external_url: string; published_at: string };

const shortDate = (iso: string) => new Date(iso).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });

// Some categories are stored as slugs ("vendor-reviews"). Show them as words.
const categoryLabel = (c: string) => {
  const words = c.replace(/[-_]+/g, " ").trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
};

// A news list whose newest item is months old makes the whole site look abandoned. If nothing has been published in
// this many days the news rows are left out.
const NEWS_FRESH_DAYS = 45;

const COMMUNITY = [
  { href: "https://reddit.com/r/BodyHackGuide", label: "r/BodyHackGuide" },
  { href: "https://reddit.com/r/BrainHackGuide", label: "r/BrainHackGuide" },
  { href: "https://discord.gg/Mhq5UdRYBA", label: "Discord" },
];

const PATHS = [
  { label: "Where to buy", href: (slug: string) => `/buy/${slug}` },
  { label: "Coupon codes", href: (slug: string) => `/coupons/${slug}` },
  { label: "Protocol", href: (slug: string) => `/protocol/${slug}` },
  { label: "Side effects", href: (slug: string) => `/side-effects/${slug}` },
];

const CARD = "home-card flex h-full min-h-[96px] flex-col p-4 sm:min-h-[176px] sm:p-5";

function BrowseMore() {
  const { data: compounds = [] } = useHomeCompounds();
  // The same six the old table showed: featured compounds in name order (useFeaturedCompounds sorted by name).
  const featured = useMemo(() => compounds.filter((c) => c.featured).slice(0, 6), [compounds]);
  return (
    <details className="home-card group mt-8">
      <summary className="flex min-h-11 cursor-pointer list-none items-center justify-between gap-3 rounded-2xl px-4 py-3 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:px-5 [&::-webkit-details-marker]:hidden">
        <span className="min-w-0">
          <span className="type-body block font-bold text-foreground">Browse more</span>
          <span className="type-small block text-muted-foreground">Where to buy, coupons, protocols, side effects</span>
        </span>
        <ChevronDown aria-hidden className="h-4 w-4 shrink-0 text-muted-foreground transition-transform duration-200 group-open:rotate-180" />
      </summary>
      <div className="border-t border-border px-4 pb-4 pt-2 sm:px-5">
        {/* From 640px these goals are chips under the goal tiles (HomeGoals); on a phone they wait in here. */}
        <div className="border-b border-border py-3 sm:hidden">
          <p className="type-small font-semibold text-foreground">More goals</p>
          <ul className="mt-1 grid grid-cols-2 gap-x-4">
            {MORE_GOALS.map((g) => (
              <li key={g.slug}>
                <Link to={`/goal/${g.slug}`} className="type-small inline-flex min-h-11 items-center font-medium text-primary underline-offset-4 hover:underline">
                  {g.label}
                </Link>
              </li>
            ))}
          </ul>
        </div>
        <ul className="divide-y divide-border">
          {featured.map((c) => (
            <li key={c.id} className="grid gap-x-6 gap-y-1 py-3 md:grid-cols-[12rem_minmax(0,1fr)] md:items-center">
              <Link to={`/compound/${c.slug}`} className="type-body inline-flex min-h-11 items-center font-semibold text-foreground underline-offset-4 hover:underline md:min-h-0">
                {c.name}
              </Link>
              <ul className="grid grid-cols-2 gap-x-4 sm:grid-cols-4">
                {PATHS.map((p) => (
                  <li key={p.label}>
                    <Link
                      to={p.href(c.slug)}
                      className="type-small inline-flex min-h-11 items-center font-medium text-primary underline-offset-4 hover:underline"
                    >
                      {p.label}
                      <span className="sr-only">: {c.name}</span>
                    </Link>
                  </li>
                ))}
              </ul>
            </li>
          ))}
        </ul>
        <ul className="mt-2 flex flex-wrap gap-x-6 border-t border-border pt-2">
          {[
            { to: "/compare", label: "All compounds" },
            { to: "/coupons", label: "All coupons" },
            { to: "/vendors", label: "All vendors" },
          ].map((l) => (
            <li key={l.to}>
              <Link to={l.to} className="type-small inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline">
                {l.label} <ArrowRight aria-hidden className="h-4 w-4" />
              </Link>
            </li>
          ))}
        </ul>
        {/* The three links of the old "Talk to other researchers" block. The navbar's community group carries them
            too, but only while its menu is open, so they stay in the page here. */}
        <div className="mt-2 border-t border-border pt-3">
          <p className="type-small font-semibold text-foreground">Community</p>
          <ul className="mt-1 flex flex-wrap gap-x-6">
            {COMMUNITY.map((c) => (
              <li key={c.href}>
                <a
                  href={c.href}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="type-small inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline"
                >
                  {c.label} <ExternalLink aria-hidden className="h-3.5 w-3.5" />
                  <span className="sr-only">(opens in a new tab)</span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      </div>
    </details>
  );
}

export default function HomeLatest() {
  const { data: posts = [], isPending: postsPending } = useQuery({
    queryKey: ["home-latest-posts"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("blog_posts")
        .select("id, title, slug, summary, category, created_at")
        .eq("published", true)
        .order("created_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as Post[];
    },
  });

  const { data: news = [] } = useQuery({
    queryKey: ["home-latest-news"],
    staleTime: 10 * 60 * 1000,
    queryFn: async () => {
      const { data, error } = await supabase
        .from("news_items")
        .select("id, title, source_name, external_url, published_at")
        .eq("published", true)
        .order("published_at", { ascending: false })
        .limit(3);
      if (error) throw error;
      return (data ?? []) as News[];
    },
  });

  const newestNews = news[0] ? new Date(news[0].published_at).getTime() : 0;
  const showNews = news.length > 0 && Date.now() - newestNews < NEWS_FRESH_DAYS * 864e5;
  const postsLoading = postsPending && posts.length === 0;

  return (
    <section aria-labelledby="home-latest-title" className={cn(container.default, gutters, homeSectionY)}>
      <HomeHeading id="home-latest-title" title="From the blog" action={{ label: "All articles", href: "/blog" }} />
      {(postsLoading || posts.length > 0) && (
        <ul className="grid gap-3 sm:gap-4 lg:grid-cols-3" aria-busy={postsLoading || undefined}>
          {postsLoading
            ? Array.from({ length: 3 }).map((_, i) => (
                <li key={i} aria-hidden className={CARD}>
                  <Skeleton className="h-3 w-32" />
                  <Skeleton className="mt-3 h-4 w-11/12" />
                  <Skeleton className="mt-2 h-4 w-2/3" />
                  <Skeleton className="mt-4 hidden h-3 w-full sm:block" />
                  <Skeleton className="mt-2 hidden h-3 w-5/6 sm:block" />
                </li>
              ))
            : posts.map((post) => (
                <li key={post.id} className="min-w-0">
                  <Link
                    to={`/blog/${post.slug}`}
                    className={cn(
                      CARD,
                      "home-card-link group focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
                    )}
                  >
                    <span className="type-label font-normal tabular-nums text-muted-foreground">
                      {post.category ? (
                        <>
                          {categoryLabel(post.category)}
                          <DotSep />
                        </>
                      ) : null}
                      {shortDate(post.created_at)}
                    </span>
                    <span className="type-body mt-2 line-clamp-2 font-bold text-foreground group-hover:underline group-hover:underline-offset-4 sm:line-clamp-3">
                      {post.title}
                    </span>
                    {post.summary && <span className="type-small mt-2 hidden text-muted-foreground sm:line-clamp-2">{post.summary}</span>}
                  </Link>
                </li>
              ))}
        </ul>
      )}

      {showNews && (
        <div className="mt-8">
          <SubHeading
            action={
              <Link to="/news" className="type-small -my-2.5 inline-flex min-h-11 items-center gap-1 font-medium text-primary underline-offset-4 hover:underline">
                All news <ArrowRight aria-hidden className="h-4 w-4" />
              </Link>
            }
          >
            In the news
          </SubHeading>
          <ul className="home-card divide-y divide-border overflow-hidden">
            {news.map((item) => (
              <li key={item.id}>
                <a href={item.external_url} target="_blank" rel="noopener noreferrer" className="group block px-4 py-3 sm:px-5">
                  <span className="type-label block font-normal tabular-nums text-muted-foreground">
                    {item.source_name ? (
                      <>
                        {item.source_name}
                        <DotSep />
                      </>
                    ) : null}
                    {shortDate(item.published_at)}
                  </span>
                  <span className="type-body mt-1 block font-semibold text-foreground group-hover:underline group-hover:underline-offset-4">
                    {item.title}
                    <ExternalLink aria-hidden className="ml-1.5 inline h-3.5 w-3.5 align-[-2px] text-muted-foreground" />
                  </span>
                </a>
              </li>
            ))}
          </ul>
        </div>
      )}

      <BrowseMore />
    </section>
  );
}
